import React, { useEffect, useRef, useState } from "react";
import { X, Save, Copy, Tag as TagIcon, Loader2, Crosshair } from "lucide-react";
import api, { formatErr } from "../lib/api";

/** Slide-in side panel showing full properties of a tag, with inline edit + save. */
export default function TagDetailsPanel({ projectId, tag, onClose, onLocate3D, hasPosition3D }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [edits, setEdits] = useState({}); // {propKey: value}
  const [nameEdit, setNameEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(""); setEdits({}); setNameEdit(null); setSaved(false);
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/tags/${encodeURIComponent(tag)}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(formatErr(e.response?.data?.detail) || "Tag not in database. Re-run the pipeline to index it.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, tag]);

  // Close on ESC
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const setEdit = (k, v) => { setEdits((p) => ({ ...p, [k]: v })); setSaved(false); };

  const save = async () => {
    if (Object.keys(edits).length === 0 && nameEdit == null) return;
    setSaving(true); setErr("");
    try {
      const payload = {};
      if (Object.keys(edits).length > 0) payload.properties = edits;
      if (nameEdit != null && nameEdit.trim()) payload.name = nameEdit.trim();
      const { data: fresh } = await api.patch(`/projects/${projectId}/tags/${encodeURIComponent(tag)}`, payload);
      setData(fresh);
      setEdits({}); setNameEdit(null); setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const copyTag = () => navigator.clipboard?.writeText(tag);

  if (!tag) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60 }} data-testid="tag-panel-backdrop" />
      <aside
        ref={panelRef}
        className="pf-grain fade-in"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(560px, 92vw)", background: "var(--pf-surface)",
          borderLeft: "1px solid var(--pf-border)", zIndex: 70,
          display: "flex", flexDirection: "column",
        }}
        data-testid="tag-details-panel"
      >
        <header style={{ padding: "14px 18px", borderBottom: "1px solid var(--pf-border)", display: "flex", alignItems: "center", gap: 10 }}>
          <TagIcon size={16} style={{ color: "var(--pf-accent)" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pf-overline">TAG · ENGINEERING DATABASE</div>
            <div className="pf-heading" style={{ fontSize: 18, letterSpacing: "-0.02em" }} data-testid="tag-panel-tag">{tag}</div>
          </div>
          <button className="btn btn-ghost" onClick={copyTag} title="Copy tag" data-testid="tag-panel-copy"><Copy size={14}/></button>
          {hasPosition3D && (
            <button className="btn btn-ghost" onClick={onLocate3D} title="Locate this tag in the 3D model" data-testid="tag-panel-locate-3d" style={{ borderColor: "var(--pf-accent)", color: "var(--pf-accent)" }}>
              <Crosshair size={14}/> Locate in 3D
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose} title="Close (Esc)" data-testid="tag-panel-close"><X size={14}/></button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--pf-text-muted)" }}><Loader2 className="pf-spin" size={14}/> Loading tag…</div>
          ) : err ? (
            <div className="pf-sev pf-sev-error" style={{ display: "block", padding: "10px 12px" }} data-testid="tag-panel-error">{err}</div>
          ) : data ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
                <Chip k="CLASS" v={data.klass} accent />
                <Chip k="TYPE" v={data.subtype} />
                <Chip k="STATUS" v={data.properties?.status || "Design"} />
              </div>

              <Section title="IDENTIFICATION">
                <Row label="Tag">{data.tag}</Row>
                <EditableRow label="Name" value={nameEdit ?? data.name ?? ""} onChange={setNameEdit} />
                <Row label="ID (internal)">{data.id_ref}</Row>
              </Section>

              {data.klass === "equipment" && (
                <>
                  <Section title="DESIGN ENVELOPE">
                    <NumEdit label="Design Temperature" unit="°C" value={data.properties?.design_temperature_c} pkey="design_temperature_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Design Pressure" unit="bar" value={data.properties?.design_pressure_bar} pkey="design_pressure_bar" edits={edits} setEdit={setEdit} />
                    <NumEdit label="MAWT" unit="°C" value={data.properties?.mawt_c} pkey="mawt_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="MAWP" unit="bar" value={data.properties?.mawp_bar} pkey="mawp_bar" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="OPERATING CONDITIONS">
                    <NumEdit label="Operating Temperature" unit="°C" value={data.properties?.operating_temperature_c} pkey="operating_temperature_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Operating Pressure" unit="bar" value={data.properties?.operating_pressure_bar} pkey="operating_pressure_bar" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Max Temperature" unit="°C" value={data.properties?.max_temperature_c} pkey="max_temperature_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Min Temperature" unit="°C" value={data.properties?.min_temperature_c} pkey="min_temperature_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Max Pressure" unit="bar" value={data.properties?.max_pressure_bar} pkey="max_pressure_bar" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Min Pressure" unit="bar" value={data.properties?.min_pressure_bar} pkey="min_pressure_bar" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="MECHANICAL">
                    <TextEdit label="Material of Construction" value={data.properties?.material_of_construction} pkey="material_of_construction" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Corrosion Allowance" unit="mm" value={data.properties?.corrosion_allowance_mm} pkey="corrosion_allowance_mm" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Insulation" value={data.properties?.insulation} pkey="insulation" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Weight" unit="kg" value={data.properties?.weight_kg} pkey="weight_kg" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="SERVICE">
                    <TextEdit label="Service" multiline value={data.properties?.service} pkey="service" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Capacity" value={data.properties?.capacity} pkey="capacity" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Applicable Code" value={data.properties?.applicable_code} pkey="applicable_code" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="PROCUREMENT">
                    <TextEdit label="Vendor" value={data.properties?.vendor} pkey="vendor" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Model" value={data.properties?.model} pkey="model" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Serial Number" value={data.properties?.serial_number} pkey="serial_number" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Datasheet Ref" value={data.properties?.datasheet_ref} pkey="datasheet_ref" edits={edits} setEdit={setEdit} />
                    <TextEdit label="P&ID Ref" value={data.properties?.pid_ref} pkey="pid_ref" edits={edits} setEdit={setEdit} />
                  </Section>
                </>
              )}

              {data.klass === "instrument" && (
                <>
                  <Section title="MEASUREMENT">
                    <Row label="Measurement Variable">{data.properties?.measurement_variable || "—"}</Row>
                    <TextEdit label="Signal Type" value={data.properties?.signal_type} pkey="signal_type" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Range (low)" unit={data.properties?.range_units} value={data.properties?.range_low} pkey="range_low" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Range (high)" unit={data.properties?.range_units} value={data.properties?.range_high} pkey="range_high" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Accuracy" unit="%" value={data.properties?.accuracy_pct} pkey="accuracy_pct" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="SET POINTS">
                    <NumEdit label="Set Point (normal)" unit={data.properties?.range_units} value={data.properties?.set_point} pkey="set_point" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Set Point (HI)" unit={data.properties?.range_units} value={data.properties?.set_point_hi} pkey="set_point_hi" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Set Point (LO)" unit={data.properties?.range_units} value={data.properties?.set_point_lo} pkey="set_point_lo" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="INSTALLATION">
                    <Row label="Location">{data.properties?.location || "—"}</Row>
                    <TextEdit label="Fail-safe Position" value={data.properties?.fail_safe} pkey="fail_safe" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Hazardous Area" value={data.properties?.hazardous_area} pkey="hazardous_area" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Loop ID" value={data.properties?.loop_id} pkey="loop_id" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="PROCUREMENT">
                    <TextEdit label="Vendor" value={data.properties?.vendor} pkey="vendor" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Model" value={data.properties?.model} pkey="model" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Serial Number" value={data.properties?.serial_number} pkey="serial_number" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Datasheet Ref" value={data.properties?.datasheet_ref} pkey="datasheet_ref" edits={edits} setEdit={setEdit} />
                  </Section>
                </>
              )}

              {data.klass === "stream" && (
                <>
                  <Section title="LINE IDENTIFICATION">
                    <TextEdit label="Line Number" value={data.properties?.line_number} pkey="line_number" edits={edits} setEdit={setEdit} />
                    <Row label="From">{data.properties?.from_equipment || "—"}</Row>
                    <Row label="To">{data.properties?.to_equipment || "—"}</Row>
                    <TextEdit label="Service" value={data.properties?.service} pkey="service" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Service Code" value={data.properties?.service_code} pkey="service_code" edits={edits} setEdit={setEdit} />
                    <Row label="Phase">{data.properties?.phase || "—"}</Row>
                  </Section>
                  <Section title="SIZING & SPEC">
                    <NumEdit label="Line Size" unit="mm" value={data.properties?.line_size_mm} pkey="line_size_mm" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Line Size" unit="inch" value={data.properties?.line_size_inches} pkey="line_size_inches" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Schedule" value={data.properties?.schedule} pkey="schedule" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Pipe Class" value={data.properties?.pipe_class} pkey="pipe_class" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Material" value={data.properties?.material} pkey="material" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Gasket Material" value={data.properties?.gasket_material} pkey="gasket_material" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Bolting Material" value={data.properties?.bolting_material} pkey="bolting_material" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="PROCESS CONDITIONS">
                    <NumEdit label="Operating T" unit="°C" value={data.properties?.operating_temperature_c} pkey="operating_temperature_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Operating P" unit="bar" value={data.properties?.operating_pressure_bar} pkey="operating_pressure_bar" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Design T" unit="°C" value={data.properties?.design_temperature_c} pkey="design_temperature_c" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Design P" unit="bar" value={data.properties?.design_pressure_bar} pkey="design_pressure_bar" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Test P (hydro)" unit="bar" value={data.properties?.test_pressure_bar} pkey="test_pressure_bar" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Flow (mass)" unit="kg/h" value={data.properties?.flow_kg_h} pkey="flow_kg_h" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Flow (volumetric)" unit="m³/h" value={data.properties?.flow_m3_h} pkey="flow_m3_h" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Density" unit="kg/m³" value={data.properties?.density_kg_m3} pkey="density_kg_m3" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Viscosity" unit="cP" value={data.properties?.viscosity_cp} pkey="viscosity_cp" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="INSULATION / TRACING / PAINT">
                    <TextEdit label="Insulation Code" value={data.properties?.insulation_code} pkey="insulation_code" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Insulation Thickness" unit="mm" value={data.properties?.insulation_thickness_mm} pkey="insulation_thickness_mm" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Tracing" value={data.properties?.tracing} pkey="tracing" edits={edits} setEdit={setEdit} />
                    <NumEdit label="Tracing Temperature" unit="°C" value={data.properties?.tracing_temperature_c} pkey="tracing_temperature_c" edits={edits} setEdit={setEdit} />
                    <TextEdit label="Paint System" value={data.properties?.paint_system} pkey="paint_system" edits={edits} setEdit={setEdit} />
                  </Section>
                  <Section title="QUALITY / SAFETY">
                    <TextEdit label="NDE %" value={data.properties?.nde} pkey="nde" edits={edits} setEdit={setEdit} />
                  </Section>
                </>
              )}

              <Section title="NOTES">
                <TextEdit label="Engineer's Notes" multiline value={data.properties?.notes} pkey="notes" edits={edits} setEdit={setEdit} />
              </Section>

              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: "var(--pf-text-dim)", marginTop: 18 }}>
                Source: {data.source_layer} · Updated {new Date(data.updated_at).toLocaleString()}
              </div>
            </>
          ) : null}
        </div>

        <footer style={{ padding: 14, borderTop: "1px solid var(--pf-border)", display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", background: "var(--pf-surface-2)" }}>
          <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
            {Object.keys(edits).length > 0 || nameEdit != null ? `${Object.keys(edits).length + (nameEdit != null ? 1 : 0)} unsaved change(s)` : "No changes"}
            {saved && <span style={{ color: "var(--pf-ok)", marginLeft: 8 }}>✓ Saved</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} data-testid="tag-panel-cancel">Close</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || (Object.keys(edits).length === 0 && nameEdit == null)} data-testid="tag-panel-save">
              {saving ? <><Loader2 className="pf-spin" size={14}/> Saving…</> : <><Save size={14}/> Save changes</>}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="pf-overline" style={{ marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--pf-border-muted)" }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, alignItems: "center", minHeight: 28 }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{label}</div>
      <div className="pf-mono" style={{ fontSize: 13, color: "var(--pf-text)" }}>{children || <span style={{ color: "var(--pf-text-dim)" }}>—</span>}</div>
    </div>
  );
}

function Chip({ k, v, accent }) {
  return (
    <div className="pf-card" style={{ padding: 8 }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{k}</div>
      <div className="pf-mono" style={{ fontSize: 12, marginTop: 2, color: accent ? "var(--pf-accent)" : "var(--pf-text)", textTransform: "uppercase" }}>{v || "—"}</div>
    </div>
  );
}

function EditableRow({ label, value, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, alignItems: "center" }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{label}</div>
      <input className="pf-input pf-mono" style={{ fontSize: 12, padding: "6px 8px" }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`tag-edit-name`} />
    </div>
  );
}

function NumEdit({ label, unit, value, pkey, edits, setEdit }) {
  const current = pkey in edits ? edits[pkey] : (value ?? "");
  const dirty = pkey in edits;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 56px", gap: 10, alignItems: "center" }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{label}</div>
      <input
        className="pf-input pf-mono"
        style={{ fontSize: 12, padding: "6px 8px", borderColor: dirty ? "var(--pf-accent)" : "var(--pf-border)" }}
        type="number" step="any"
        value={current === null || current === undefined ? "" : current}
        onChange={(e) => { const v = e.target.value; setEdit(pkey, v === "" ? null : Number(v)); }}
        data-testid={`tag-edit-${pkey}`}
      />
      <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)", textAlign: "right" }}>{unit || ""}</div>
    </div>
  );
}

function TextEdit({ label, value, pkey, edits, setEdit, multiline }) {
  const current = pkey in edits ? edits[pkey] : (value ?? "");
  const dirty = pkey in edits;
  if (multiline) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, alignItems: "start" }}>
        <div className="pf-overline" style={{ fontSize: 9, marginTop: 6 }}>{label}</div>
        <textarea
          className="pf-input pf-mono"
          style={{ fontSize: 12, padding: "6px 8px", minHeight: 52, borderColor: dirty ? "var(--pf-accent)" : "var(--pf-border)" }}
          value={current ?? ""}
          onChange={(e) => setEdit(pkey, e.target.value)}
          data-testid={`tag-edit-${pkey}`}
        />
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, alignItems: "center" }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{label}</div>
      <input
        className="pf-input pf-mono"
        style={{ fontSize: 12, padding: "6px 8px", borderColor: dirty ? "var(--pf-accent)" : "var(--pf-border)" }}
        value={current ?? ""}
        onChange={(e) => setEdit(pkey, e.target.value)}
        data-testid={`tag-edit-${pkey}`}
      />
    </div>
  );
}
